// File: src/pages/Login.js
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { LuArrowLeft, LuMail, LuLock, LuEye, LuEyeOff } from 'react-icons/lu';
import { useToaster } from '../context/ToasterContext';
import { ReactComponent as TribeLogo } from '../assets/TribeLogo.svg';
import MinimalFooter from '../components/MinimalFooter';

function getLoginButtonStyle(isActive) {
  return {
    width: '100%',
    maxWidth: 400,
    height: 42,
    padding: '0 14px',
    fontSize: 12.5,
    border: 'none',
    borderRadius: 10,
    color: '#fff',
    cursor: isActive ? 'pointer' : 'not-allowed',
    fontWeight: 800,
    letterSpacing: '-0.01em',
    marginTop: 0,
    background: isActive ? '#0b0b0b' : '#bdbdbd',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    WebkitTapHighlightColor: 'transparent',
  };
}

export default function Login() {
  console.log('[Login.js] Rendering Login component...');
  const navigate = useNavigate();
  const { handleLoginSuccess } = useContext(AuthContext);
  const { addToast } = useToaster();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [shakeInputs, setShakeInputs] = useState(false);

  const socketRef = useRef(null);

  useEffect(() => {
    const reason = localStorage.getItem('logoutReason');
    if (reason) {
      addToast(reason, 'info');
      localStorage.removeItem('logoutReason');
    }
  }, [addToast]);

  useEffect(() => {
    const accessToken =
      JSON.parse(localStorage.getItem('tribe_auth') || '{}')?.token ||
      localStorage.getItem('accessToken') ||
      '';

    const socket = io(`${process.env.REACT_APP_API_URL}`, {
      auth: { token: accessToken },
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 300,
      reconnectionDelayMax: 3000,
      timeout: 8000,
    });

    socketRef.current = socket;

    const onConnect = () => {
      console.log('[Login.js] socket connected', socket.id);

      const fresh =
        JSON.parse(localStorage.getItem('tribe_auth') || '{}')?.token ||
        localStorage.getItem('accessToken') ||
        accessToken;

      try {
        socket.auth = { token: fresh };
      } catch {}
    };

    const onForceLogout = (data) => {
      console.log('[Login.js] Received forceLogout =>', data);

      alert(data.message || 'You have been logged out from another session.');

      localStorage.removeItem('userId');
      localStorage.removeItem('userName');
      localStorage.removeItem('sessionId');
      localStorage.removeItem('jwt');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');

      try {
        socket.disconnect();
      } catch {}

      navigate('/');
    };

    const onError = (e) => console.warn('[Login.js] socket error', e?.message || e);

    socket.on('connect', onConnect);
    socket.on('connect_error', onError);
    socket.on('forceLogout', onForceLogout);

    return () => {
      console.log('[Login.js] Cleaning up forceLogout listener.');

      socket.off('connect', onConnect);
      socket.off('connect_error', onError);
      socket.off('forceLogout', onForceLogout);

      try {
        socket.disconnect();
      } catch {}

      socketRef.current = null;
    };
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();

    console.log('[Login.js] handleLogin => email:', email, 'password length:', password.length);

    try {
      console.log('[Login.js] Sending POST /auth/login');

      const res = await axios.post(`${process.env.REACT_APP_API_URL}/auth/login`, {
        email,
        password,
      });

      console.log('[Login.js] /auth/login response =>', res.data);

      if (res.data.userId) {
        console.log('[Login.js] userId returned =>', res.data.userId);

        handleLoginSuccess(res.data.userId);

        if (res.data.isBanned) {
          addToast(
            `You are banned. Reason: ${res.data.banReason || 'No reason provided'}`,
            'error'
          );
        } else if (res.data.isTempBanned) {
          addToast(
            `You are temporarily banned. Reason: ${res.data.tempBanReason}`,
            'error'
          );
        } else {
          const firstName = res.data.firstName || '';
          const lastInit = res.data.lastName ? res.data.lastName.charAt(0) + '.' : '';
          addToast(`Welcome back ${firstName} ${lastInit}`, 'success');
        }

        if (res.data.token) {
          localStorage.setItem('jwt', res.data.token);
        }

        if (res.data.accessToken) {
          localStorage.setItem('accessToken', res.data.accessToken);
        }

        if (res.data.refreshToken) {
          localStorage.setItem('refreshToken', res.data.refreshToken);
        }

        if (res.data.sessionId) {
          localStorage.setItem('sessionId', res.data.sessionId);

          const s = socketRef.current;

          if (s && s.connected) {
            const fresh =
              JSON.parse(localStorage.getItem('tribe_auth') || '{}')?.token ||
              localStorage.getItem('accessToken') ||
              res.data.accessToken ||
              '';

            try {
              s.auth = { token: fresh };
            } catch {}

            s.emit('registerSession', { sessionId: res.data.sessionId });
          }
        }

        navigate('/events');
      }
    } catch (err) {
      console.warn('[Login.js] handleLogin => error =>', err);

      const msg = err.response?.data?.message || err.message;
      addToast(msg, 'error');

      setShakeInputs(true);

      setTimeout(() => {
        setShakeInputs(false);
        setPassword('');
      }, 300);
    }
  };

  const isButtonActive = email.trim() && password.trim();

  const pageStyle = {
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    backgroundColor: '#fff',
    minHeight: '80vh',
    margin: 0,
    position: 'relative',
    overflow: 'hidden',
  };

  const backgroundWrapperStyle = {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 0,
    display: 'none',
    zIndex: 1,
  };

  const contentShellStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    zIndex: 5,
    position: 'relative',
    minHeight: '81vh',
    padding: '1rem',
  };

  const logoWrapStyle = {
    marginTop: '0.5rem',
    marginBottom: 20,
    cursor: 'pointer',
  };

  const logoStyle = {
    width: 100,
    height: 'auto',
  };

  const containerStyle = {
    width: '100%',
    maxWidth: 320,
    margin: '0 auto',
    textAlign: 'center',
    zIndex: 5,
    position: 'relative',
  };

  const titleStyle = {
    margin: 0,
    fontSize: '1.6rem',
    lineHeight: 1,
    fontWeight: 900,
    color: '#0b0b0b',
    letterSpacing: '-0.045em',
  };

  const subtitleStyle = {
    margin: '10px 0 26px',
    fontSize: '0.9rem',
    lineHeight: 1.35,
    color: 'rgba(17,17,17,0.62)',
    fontWeight: 500,
    letterSpacing: '-0.01em',
  };

  const createAccountStyle = {
    marginLeft: 4,
    color: '#0b0b0b',
    fontWeight: 850,
    cursor: 'pointer',
    textDecoration: 'none',
  };

  const formStyle = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    alignItems: 'center',
  };

  const inputWrapStyle = {
    width: '100%',
    height: 48,
    background: '#f5f5f5',
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    padding: '0 14px',
    boxSizing: 'border-box',
    marginBottom: 12,
    border: shakeInputs ? '1px solid #C22D2D' : '1px solid transparent',
  };

  const inputIconStyle = {
  fontSize: '1.05rem',
  color: '#0b0b0b',
  marginRight: 10,
  flexShrink: 0,
};

const eyeButtonStyle = {
  border: 'none',
  background: 'transparent',
  padding: 0,
  marginLeft: 10,
  cursor: 'pointer',
  color: '#0b0b0b',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 20,
  flexShrink: 0,
};
  const inputStyle = {
    flex: 1,
    minWidth: 0,
    height: '100%',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    color: '#0b0b0b',
    fontSize: '0.9rem',
    fontWeight: 520,
    letterSpacing: '-0.01em',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  };

  const forgotWrapStyle = {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 0,
    marginBottom: 24,
  };

  const forgotLinkStyle = {
    fontSize: '0.8rem',
    color: '#0b0b0b',
    cursor: 'pointer',
    textDecoration: 'none',
    fontWeight: 650,
    letterSpacing: '-0.01em',
  };

  const bottomStyle = {
    marginTop: 24,
    fontSize: '0.9rem',
    color: 'rgba(17,17,17,0.62)',
    textAlign: 'center',
    fontWeight: 500,
    letterSpacing: '-0.012em',
  };

  const signUpLinkStyle = {
    marginLeft: 6,
    cursor: 'pointer',
    color: '#0b0b0b',
    fontWeight: 850,
    textDecoration: 'none',
  };

  return (
    <>
      <style>
        {`
          @keyframes shake {
            0% { transform: translateX(0); }
            20% { transform: translateX(-5px); }
            40% { transform: translateX(5px); }
            60% { transform: translateX(-5px); }
            80% { transform: translateX(5px); }
            100% { transform: translateX(0); }
          }

          .shake {
            animation: shake 0.3s ease;
          }

          .login-field::placeholder {
            color: rgba(0,0,0,0.46);
          }
        `}
      </style>

      {/* NAV BAR - unchanged */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '1rem',
        zIndex: 4,
        position: 'relative',
        background: 'none',
        marginBottom: '1rem',
      }}>
        <button
          style={{
            backgroundColor: '#000',
            color: '#fff',
            border: 'none',
            borderRadius: 9999,
            padding: '0px 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: '1rem',
            height: 28,
          }}
          onClick={() => {
            console.log('[Login.js] back button clicked => navigate("/events")');
            navigate('/events');
          }}
        >
          <LuArrowLeft />
        </button>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#eee',
            borderRadius: 9999,
            overflow: 'hidden',
            padding: '4px',
            height: 28,
          }}
        >
          <div
            style={{
              padding: '0.4rem 0.7rem',
              borderRadius: 9999,
              cursor: 'pointer',
              fontSize: '0.75rem',
              fontWeight: 600,
              color: '#000',
              backgroundColor: '#fff',
              transition: 'background-color 0.2s ease',
              userSelect: 'none',
            }}
          >
            Login
          </div>
          <div
            style={{
              padding: '0.4rem 0.7rem',
              borderRadius: 9999,
              cursor: 'pointer',
              fontSize: '0.75rem',
              fontWeight: 600,
              color: '#555',
              backgroundColor: 'transparent',
              transition: 'background-color 0.2s ease',
              userSelect: 'none',
            }}
            onClick={() => {
              console.log('[Login.js] toggling to "Sign Up" => navigate("/register")');
              navigate('/register');
            }}
          >
            Sign Up
          </div>
        </div>
      </div>

      {/* Main page below navbar */}
      <div style={pageStyle}>
        <div style={backgroundWrapperStyle} />

        <div style={contentShellStyle}>
          <div style={logoWrapStyle} onClick={() => navigate('/events')}>
            <TribeLogo style={logoStyle} />
          </div>

          <div
            className={shakeInputs ? 'shake' : ''}
            style={containerStyle}
          >
            <h1 style={titleStyle}>WELCOME BACK</h1>

            <p style={subtitleStyle}>
              New user?
              <span
                style={createAccountStyle}
                onClick={() => {
                  console.log('[Login.js] "Create an account" => navigate("/register")');
                  navigate('/register');
                }}
              >
                Create an account
              </span>
            </p>

            <form onSubmit={handleLogin} style={formStyle}>
              <div style={inputWrapStyle}>
                <LuMail style={inputIconStyle} />

                <input
                  className="login-field"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    console.log('[Login.js] email changed =>', e.target.value);
                    setEmail(e.target.value);
                  }}
                  required
                  placeholder="Email Address"
                  style={inputStyle}
                />
              </div>

              <div style={inputWrapStyle}>
                <LuLock style={inputIconStyle} />

                <input
                  className="login-field"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    console.log('[Login.js] password changed => length:', e.target.value.length);
                    setPassword(e.target.value);
                  }}
                  required
                  placeholder="Password"
                  style={inputStyle}
                />

                <button
                  type="button"
                  style={eyeButtonStyle}
                  onClick={() => {
                    console.log('[Login.js] toggling showPassword =>', !showPassword);
                    setShowPassword((prev) => !prev);
                  }}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <LuEyeOff /> : <LuEye />}
                </button>
              </div>

              <div style={forgotWrapStyle}>
                <span
                  style={forgotLinkStyle}
                  onClick={() => {
                    console.log('[Login.js] Forgot Password => navigate("/forgot-password")');
                    navigate('/forgot-password');
                  }}
                >
                  Forgot password?
                </span>
              </div>

              <button
                type="submit"
                style={getLoginButtonStyle(!!isButtonActive)}
                disabled={!isButtonActive}
              >
                Login
              </button>
            </form>

            <div style={bottomStyle}>
              Don’t have an account?
              <span
                style={signUpLinkStyle}
                onClick={() => {
                  console.log('[Login.js] "Sign up" => navigate("/register")');
                  navigate('/register');
                }}
              >
                Sign up
              </span>
            </div>
          </div>
        </div>
      </div>

      <MinimalFooter />
    </>
  );
}